<template>
  <!-- Hub UI W7.1 (PO Q10): Chinese Naive locale app-wide (weeks start on Monday) with
       every confirm label 確認 — config/naiveLocale.ts. -->
  <n-config-provider
    :theme-overrides="themeOverrides"
    :locale="NAIVE_LOCALE"
    :date-locale="NAIVE_DATE_LOCALE"
  >
    <n-message-provider>
      <n-notification-provider>
        <n-dialog-provider>
          <n-loading-bar-provider>
            <!-- Global Version Checker: Automatically detects updates and reloads the app -->
            <GlobalVersionChecker />

            <!-- This will show a loading spinner until Firebase has initialized -->
            <div
              v-if="!authStore.isReady"
              style="
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background-color: white;
              "
            >
              <n-spin size="large" />
              <div style="margin-top: 16px; font-size: 1.125rem; color: #4a5568">系統載入中...</div>
            </div>
            <!-- Once ready, the main application is rendered -->
            <router-view v-else />
          </n-loading-bar-provider>
        </n-dialog-provider>
      </n-notification-provider>
    </n-message-provider>
  </n-config-provider>
</template>

<script setup lang="ts">
import { onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  NMessageProvider,
  NNotificationProvider,
  NDialogProvider,
  NLoadingBarProvider,
  NSpin,
  NConfigProvider,
} from 'naive-ui';
import { useAuthStore } from 'stores/auth';
import { UI_CONFIG } from './config/appConfig';
import { NAIVE_DATE_LOCALE, NAIVE_LOCALE } from './config/naiveLocale';
import { isLifeguardMobileUser } from './config/permissions';
import GlobalVersionChecker from '@/components/GlobalVersionChecker.vue';
const authStore = useAuthStore();
const route = useRoute();

// Role-aware manifest selection. iOS uses the manifest's `start_url` for the
// home-screen bookmark, so picking the wrong manifest here means the PWA opens
// to the wrong page (e.g. a teacher landing on the kiosk start_url, then being
// bounced by the kiosk access guard — Carol's case).
const updateManifest = () => {
  const path = route.path;
  const isMobileUA = /Mobi|Android/i.test(navigator.userAgent);
  const userRoles = authStore.userRoles ?? [];
  const isKioskOnly = userRoles.length === 1 && userRoles.includes('kiosk');
  const isTeacher = userRoles.includes('teacher');

  // iOS ignores manifest icons for Add-to-Home-Screen — it reads the
  // apple-touch-icon <link>. Swap it in lockstep with the manifest so the
  // 打卡 icon (clock-in.png) appears when installing from the clock screen,
  // and the brand logo everywhere else.
  const appleIconHref = path.startsWith('/attendance/clock')
    ? '/icons/clock-in.png'
    : '/icons/oovi-logo.png';
  const appleIconEl = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (appleIconEl && !appleIconEl.href.endsWith(appleIconHref)) {
    appleIconEl.href = appleIconHref;
  }

  let target: string | null = null;
  if (path.startsWith('/attendance/clock')) {
    // Punch-screen PWA: home-screen icon opens directly to /attendance/clock
    // (start_url), full-screen standalone. Path-gated so this only wins while the
    // user is actually on the clock screen at install time — matches the existing
    // kiosk/teacher per-context pattern. start_url is staff-role-safe (all staff
    // roles may access /attendance/clock; access is gated server-side per account).
    target = '/manifest.attendance.json';
  } else if (path.startsWith('/kiosk') || isKioskOnly) {
    target = '/manifest.kiosk.json';
  } else if (isMobileUA && isLifeguardMobileUser(userRoles)) {
    // 救生員 home-screen app opens their phone home (start_url /teacher/home),
    // wherever they install it from (上課名單, 我的薪資單, 小工具).
    target = '/manifest.lifeguard.json';
  } else if (path.startsWith('/teacher/mobile') || (isTeacher && isMobileUA)) {
    target = '/manifest.teacher.json';
  }

  let manifestElement = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!target) {
    if (manifestElement) manifestElement.remove();
    return;
  }
  if (!manifestElement) {
    manifestElement = document.createElement('link');
    manifestElement.rel = 'manifest';
    document.head.appendChild(manifestElement);
  }
  if (!manifestElement.href.endsWith(target)) {
    manifestElement.href = target;
  }
};

// React to both route and role changes — userRoles may populate after the
// initial route resolution when authStore finishes init.
watch([() => route.path, () => authStore.userRoles], () => updateManifest(), { immediate: true });

const isMobileRoute = computed(() => route.path.startsWith('/teacher/mobile'));

const themeOverrides = computed(() => {
  return isMobileRoute.value ? UI_CONFIG.MOBILE_THEME_OVERRIDES : UI_CONFIG.THEME_OVERRIDES;
});

onMounted(() => {
  // The watcher with immediate: true handles the initial manifest setting,
  // so no additional logic is needed here for the manifest.
});
</script>
